'use client';

import { useState, useMemo } from "react";
import { MapPin, Users, BriefcaseBusiness } from "lucide-react";
import { usePageContent } from "@/hooks/useContent";
import Filters from "@/components/Filters";
import EmptyState from "@/components/EmptyState";
import JobCard from "./JobCard";
import type { Community } from "@/types";

export default function CareersClient() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Get careers content from centralized data
  const careersContent = usePageContent('careers') as Community["careers"]

  // Get all unique categories
  const categories = useMemo(() => {
    if (!careersContent?.jobs) return ["all"];
    return ["all", ...new Set(careersContent.jobs.map(cat => cat.department))];
  }, [careersContent]);

  // Filter jobs based on search and category
  const filteredData = useMemo(() => {
    if (!careersContent?.jobs) return [];
    
    let filtered = careersContent.jobs;

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(category => category.department === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(job =>
          job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
    }

    return filtered;
  }, [searchTerm, selectedCategory, careersContent]);

  // Get total job count
  const totalJobs = useMemo(() => {
    return filteredData.length;
  }, [filteredData]);

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <Header totalJobs={totalJobs} careersContent={careersContent} />

      <Filters 
        searchTerm={searchTerm} 
        setSearchTerm={setSearchTerm} 
        setSelectedCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        categories={categories}
        placeholder="Search jobs..."
      />

      {filteredData.length === 0 ? (
        <EmptyState
          title={careersContent.emptyState.title}
          description={careersContent.emptyState.description}
          buttonText={careersContent.emptyState.buttonText}
          onClear={() => {}}
        />
      ) : (
        <div className="space-y-4">
          {filteredData.length === 0 ? (
            <EmptyState
              title={careersContent.notFoundSearch.title}
              description={careersContent.notFoundSearch.description}
              buttonText={careersContent.notFoundSearch.buttonText}
              onClear={() => {
                setSearchTerm("");
              }}
            />
          ) : (
            filteredData.map((job) => (
                <JobCard 
                    key={job.id}
                    job={job}
                />
            ))
          )}
        </div>
      )}
    </div>
  );
}

const Header = ({ totalJobs, careersContent }: { 
  totalJobs: number; 
  careersContent: Community["careers"]
}) => {
  return (
    <div className="text-center">
      <span className="iconBudg-v2 size-12 mx-auto mb-2">
        <BriefcaseBusiness className="size-8" />
      </span>
      <h1 className="title-size font-bold text-bg mb-2">
        {careersContent.title}
      </h1>
      <h2 className="text-base lg:text-lg font-bold text-gray-900 mb-4">
        {careersContent.subtitle}
      </h2>
      <p className="text-sm lg:text-base text-gray-600 max-w-2xl mx-auto mb-4">
        {careersContent.description}
      </p>
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-sm text-gray-500">
        <span className="flex items-center gap-1">
          <Users className="w-4 h-4" />
          {totalJobs} {careersContent.stats[0].label}
        </span>
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          {careersContent.stats[1].label}
        </span>
      </div>
    </div>
  )
}